/** @jsxImportSource @emotion/react */
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import * as authSelectors from "../../auth/authSelectors";
import { Drawer } from "../../../components/Drawer";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { StyledForm } from "../../../components/StyledForm";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { css } from "@emotion/react";
import { useEffect, useState } from "react";
import "react18-json-view/src/style.css";
import { getErrorMessage } from "../../../error-handler";
import { SwitchInput } from "../../../components/forms/inputs/SwitchInput";
import {
  DeliveryProviderConfiguration,
  MutateDeliveryProviderConfigurationPayload,
} from "../models/delivery-provider-configuration";
import {
  useCreateDeliveryProviderConfiguration,
  useUpdateDeliveryProviderConfiguration,
} from "../service/delivery-provider-configrations-api";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";

enum DeliveryProvider {
  foodora_go = "foodora_go",
  jet_daas = "jet_daas",
  woltdrive = "woltdrive",
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  deliveryProviderConfiguration?: DeliveryProviderConfiguration;
  existingDeliveryProviders?: string[];
};

const additionalFormStyles = css`
  gap: 16px;
`;

const validationSchema = yup.object({
  name: yup.string().required("Enter name to continue"),
  provider: yup.string().required("Enter provider to continue"),
  isEnabled: yup.boolean(),
  apiKey: yup.string().when("provider", {
    is: (value: string) => {
      return (
        value === DeliveryProvider.foodora_go ||
        value === DeliveryProvider.jet_daas ||
        value === DeliveryProvider.woltdrive
      );
    },
    then: (schema) => schema.required("API key is required"),
    otherwise: (schema) => schema.optional(),
  }),
  baseUrl: yup.string().when("provider", {
    is: (value: string) => {
      return (
        value === DeliveryProvider.foodora_go ||
        value === DeliveryProvider.jet_daas ||
        value === DeliveryProvider.woltdrive
      );
    },
    then: (schema) => schema.required("Base URL is required"),
    otherwise: (schema) => schema.optional(),
  }),
  clientId: yup.string().when("provider", {
    is: (value: string) => {
      return (
        value === DeliveryProvider.foodora_go ||
        value === DeliveryProvider.jet_daas ||
        value === DeliveryProvider.woltdrive
      );
    },
    then: (schema) => schema.required("Client ID is required"),
    otherwise: (schema) => schema.optional(),
  }),
  isTesting: yup.boolean(),
  extras: yup.object().when("provider", {
    is: DeliveryProvider.foodora_go,
    then: yup.object({
      scope: yup.string().required("Scope is required"),
      baseUrlAuth: yup.string(),
    }),
    otherwise: yup.object().when("provider", {
      is: DeliveryProvider.jet_daas,
      then: yup.object({
        scope: yup.string(),
        baseUrlAuth: yup.string().required("Base URL auth is required"),
      }),
      otherwise: yup.object({
        scope: yup.string(),
        baseUrlAuth: yup.string(),
      }),
    }),
  }),
});

export const MutateDeliveryProviderConfigurationDrawer = ({
  isOpen,
  onClose,
  deliveryProviderConfiguration,
  existingDeliveryProviders = [],
}: Props) => {
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const [isLoadingMutation, setIsLoadingMutation] = useState(false);
  const isEdit = !!deliveryProviderConfiguration?.id;

  const methods = useForm<MutateDeliveryProviderConfigurationPayload>({
    defaultValues: {
      name: "",
      provider: "",
      apiKey: "",
      baseUrl: "",
      clientId: "",
      isEnabled: true,
      isTesting: false,
      extras: {
        baseUrlAuth: "",
        scope: "",
      },
    },
    resolver: yupResolver(validationSchema),
  });

  // TODO when API for fetching delivery providers is in place
  // const { data: deliveryProviders } = useGetDeliveryProviders(
  //   organizationKeyName
  // );

  const deliveryProviders = [
    { name: "FoodoraGo", value: "foodora_go" },
    { name: "Rider", value: "rider" },
    { name: "Just-Eat Delivery", value: "jet_daas" },
    { name: "Wolt Drive", value: "woltdrive" },
  ];

  const {
    mutateAsync: createDeliveryProviderConfiguration,
  } = useCreateDeliveryProviderConfiguration();

  const {
    mutateAsync: updateDeliveryProviderConfiguration,
  } = useUpdateDeliveryProviderConfiguration();

  const handleOnClose = () => {
    methods.reset();
    onClose();
  };

  const onSubmit = async (data: MutateDeliveryProviderConfigurationPayload) => {
    setIsLoadingMutation(true);

    try {
      if (isEdit) {
        await updateDeliveryProviderConfiguration({
          organizationKeyName,
          data: {
            id: deliveryProviderConfiguration.id,
            ...data,
          },
        });
      } else {
        await createDeliveryProviderConfiguration({
          data: {
            ...data,
          },
          organizationKeyName,
        });
      }
      toastr.success(
        "Success",
        `Delivery provider configuration successfully ${
          deliveryProviderConfiguration?.id ? "updated" : "created"
        }`
      );
      handleOnClose();
      setIsLoadingMutation(false);
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
      setIsLoadingMutation(false);
    }
  };

  useEffect(() => {
    if (!isEdit) {
      return;
    }

    methods.reset({
      name: deliveryProviderConfiguration.name,
      provider: deliveryProviderConfiguration.provider,
      apiKey: deliveryProviderConfiguration.apiKey,
      baseUrl: deliveryProviderConfiguration.baseUrl,
      clientId: deliveryProviderConfiguration.clientId,
      isEnabled: deliveryProviderConfiguration.isEnabled,
      isTesting: deliveryProviderConfiguration.isTesting,
      extras: {
        baseUrlAuth: deliveryProviderConfiguration.extras?.baseUrlAuth,
        scope: deliveryProviderConfiguration.extras?.scope,
      },
    });
  }, [deliveryProviderConfiguration, methods, isEdit]);

  return (
    <Drawer
      title={`${
        deliveryProviderConfiguration?.id ? "Edit" : "Create"
      } delivery provider configuration`}
      width={500}
      buttons={
        <ButtonAction
          disabled={isLoadingMutation}
          onClick={methods.handleSubmit(onSubmit)}
          type="button"
        >
          {isLoadingMutation ? <ActionSpinner /> : "Save"}
        </ButtonAction>
      }
      open={isOpen}
      onClose={handleOnClose}
    >
      <StyledForm additionalStyles={additionalFormStyles}>
        <FormProvider {...methods}>
          <SelectInput
            label="Provider"
            name="provider"
            isDisabled={!!deliveryProviderConfiguration?.id}
            placeholder="Select provider"
            options={(deliveryProviders || []).map((deliveryProvider) => ({
              label: deliveryProvider.name,
              value: deliveryProvider.value,
            }))}
          />
          <TextInput label="Name" name="name" placeholder="Enter name" />
          <TextInput
            label="API key"
            name="apiKey"
            placeholder="Enter API key"
          />
          <TextInput
            label="Base URL"
            name="baseUrl"
            placeholder="Enter base URL"
          />
          <TextInput
            label="Client id"
            name="clientId"
            placeholder="Enter client id"
          />
          <SwitchInput label="Is enabled?" name="isEnabled" />
          <SwitchInput label="Is for testing?" name="isTesting" />
          <TextInput
            label="Base URL auth"
            name="extras.baseUrlAuth"
            placeholder="Enter base URL auth"
          />
          <TextInput
            label="Scope"
            name="extras.scope"
            placeholder="Enter scope"
          />
        </FormProvider>
      </StyledForm>
    </Drawer>
  );
};
